import type { Readable, Writable } from 'svelte/store';

import type { ConfirmDialog } from '@components/common/confirm-dialog';
import type { Signup } from '@components/layout/auth/signup';
import type { IncomeDialog } from '@components/layout/header/components/income-dialog';
import type { BuyMembershipDialog } from '@components/payment/dialog/buy-membership-dialog';
import type { BuyMinutesDialog } from '@components/payment/dialog/buy-minutes-dialog';
import type { SpinnerDialog } from '@components/payment/dialog/spinner-dialog';
import type {
    DeactivateDialog,
    DeleteDialog,
} from '@components/profile/forms/deactivate/components';
import type { RatingOperatorDialog } from '@components/rating/operator-dialog';
import type { RatingUserDialog } from '@components/rating/user-dialog';
import type { GiftChooseDialog } from '@components/room/gift/choose-dialog';
import type { GiftConfirmDialog } from '@components/room/gift/confirm-dialog';
import { ReportReasonsDialog } from '@components/room/reportReasons';
import type { SelfInfoDialog } from '@components/signup/self-info-dialog';
import type { UserAgreementDialog } from '@components/signup/user-agreement-dialog';
import { UserNoteFormDialog } from '@components/user-note';

import type { ComponentProps } from 'svelte';

type DialogParams = {
    containerWrapperClasses?: string;
    containerClasses?: string;
    backdropClasses?: string;
    closeHandler?: () => void;
    isBottomSheet?: boolean;
};

export type DialogProps = DialogParams & (
    | ComponentProps<Signup>
    | ComponentProps<ReportReasonsDialog>
    | ComponentProps<IncomeDialog>
    | ComponentProps<BuyMembershipDialog>
    | ComponentProps<RatingOperatorDialog>
    | ComponentProps<RatingUserDialog>
    | ComponentProps<SelfInfoDialog>
    | ComponentProps<BuyMinutesDialog>
    | ComponentProps<UserAgreementDialog>
    | ComponentProps<ConfirmDialog>
    | ComponentProps<DeactivateDialog>
    | ComponentProps<DeleteDialog>
    | ComponentProps<GiftChooseDialog>
    | ComponentProps<GiftConfirmDialog>
    | ComponentProps<UserNoteFormDialog>
    | ComponentProps<SpinnerDialog>
);

export interface DialogStoreState {
    isOpened: boolean;
    isHaveCloseBtn?: boolean;
    closeByOutsideClick?: boolean;
    closeHandler?: () => void;
    dialogType?: DialogTypes | undefined;
    props?: DialogProps;
}

export interface DialogStore extends Readable<DialogStoreState> {
    store: Writable<DialogStoreState>;
    closeDialog: () => void;
    openDialog: (
        dialogType: DialogTypes,
        props?: DialogProps,
        params?: { isHaveCloseBtn?: boolean; closeByOutsideClick?: boolean },
    ) => void;
    showLogin: (closeHandler?: () => void) => void;
    showSignup: (props?: Partial<ComponentProps<Signup>>, closeHandler?: () => void, lightBackdrop?: boolean) => void;
    showSelfInfo: (props: ComponentProps<SelfInfoDialog>, isHaveCloseBtn?: boolean) => void;
    showIncome: (props: ComponentProps<IncomeDialog>) => void;
    showRatingOperator: (props: ComponentProps<RatingOperatorDialog>, closeHandler: () => void) => void;
    showRatingUser: (props: ComponentProps<RatingUserDialog>, closeHandler: () => void) => void;
    showGiftChooseDialog: (props: ComponentProps<GiftChooseDialog>) => void;
    showGiftConfirmDialog: (props: ComponentProps<GiftConfirmDialog>, closeHandler: () => void) => void;
    showVpnDetected: () => void;
    showUnsupportedBrowser: () => void;
    showBuyMembership: (
      props: Partial<ComponentProps<BuyMembershipDialog>>,
      closeHandler?: () => void,
      lightBackdrop?: boolean
    ) => void;
    showBuyMinutes: (props: ComponentProps<BuyMinutesDialog>) => void;
    showReportReasons: (props: ComponentProps<ReportReasonsDialog>) => void;
    showResetPassword: () => void;
    showResetPasswordLinkSent: () => void;
    getState: () => DialogStoreState;
    showUserAgreement: (props: ComponentProps<UserAgreementDialog>, isHaveCloseBtn?: boolean) => void;
    showConfirm: (props: ComponentProps<ConfirmDialog>, closeHandler?: () => void) => void;
    showSpinnerDialog: (props?: ComponentProps<SpinnerDialog>) => void;
    showDeactivateAccount: (props?: ComponentProps<DeactivateDialog>) => void;
    showDeleteAccount: (props?: ComponentProps<DeleteDialog>) => void;
    showDisablePackageMinutesAutoRenew: () => void;
    showUserNoteDialog: (
      props: { userDisplayName: string; textStore: Writable<string> },
      closeHandler: () => void
    ) => void;
}

export enum DialogTypes {
    Login = 'login',
    VpnDetected = 'vpnDetected',
    UnsupportedBrowser = 'unsupportedBrowser',
    Signup = 'signup',
    GiftChoose = 'giftChoose',
    GiftConfirm = 'giftConfirm',
    SpinnerDialog = 'spinnerDialog',
    SelfInfo = 'selfInfo',
    Income = 'income',
    BuyMembership = 'buyMembership',
    BuyMinutes = 'buyMinutes',
    ReportReasons = 'reportReasons',
    RoomClosed = 'roomClosed',
    ResetPassword = 'resetPassword',
    ResetPasswordLinkSent = 'resetPasswordLinkSent',
    UserAgreement = 'userAgreement',
    Confirm = 'confirm',
    DeactivateAccount = 'deactivateAccount',
    DeleteAccount = 'deleteAccount',
    RatingOperator = 'ratingOperator',
    RatingUser = 'ratingUser',
    DisablePackageMinutesAutoRenew = 'disablePackageMinutesAutoRenew',
    UserNote = 'userNote',
}
